import type { ReactNode } from 'react';
import { Button } from './Button';

/**
 * Подсказка под содержимым.
 *
 * Цвет отмечает исключение, а не факт: `warn` и `crit` ставятся там,
 * где что-то вышло за норму, а не просто чтобы обратить внимание.
 */
export function Note({ tone, children }: { tone?: 'warn' | 'crit'; children: ReactNode }) {
  return (
    <div className={tone ? `note ${tone}` : 'note'}>
      <span className="d" />
      <span>{children}</span>
    </div>
  );
}

/**
 * Врезка, требующая действия. Красный — только на необратимом
 * и просроченном (чек-лист кита, п. 3). `mut` — факт без действия
 * («Проиграна 30.09»), `info` — состояние, которое ждёт («В резерве с …»).
 */
export function Alert({
  tone,
  children,
  action,
}: {
  tone?: 'crit' | 'mut' | 'info';
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={tone ? `alert ${tone}` : 'alert'}>
      {children}
      {action ? (
        <>
          <span className="sp" />
          {action}
        </>
      ) : null}
    </div>
  );
}

/** Пустое состояние — одно из трёх обязательных у каждого экрана (ТЗ §7). */
export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/** Загрузка скелетом. */
export function Skeleton({ width, height = 14 }: { width?: number | string; height?: number }) {
  return <div className="skel" style={{ width: width ?? '100%', height }} />;
}

/**
 * Скелет таблицы: несколько строк примерно той же высоты, что настоящие.
 * Скелет не той формы хуже, чем его отсутствие — экран дёргается.
 */
export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <table className="tbl">
      <tbody>
        {Array.from({ length: rows }, (_, r) => (
          <tr key={r}>
            {Array.from({ length: cols }, (_, c) => (
              <td key={c}>
                <Skeleton width={c === 0 ? '60%' : '40%'} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Ошибка загрузки — третье обязательное состояние. По киту это красная
 * плашка `.alert.crit`: что не вышло, текст сервера как есть и повтор
 * кнопкой внутри плашки.
 */
export function ErrorState({
  title = 'Не загрузилось.',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Alert
      tone="crit"
      action={
        onRetry ? (
          <Button small onClick={onRetry}>
            Повторить
          </Button>
        ) : undefined
      }
    >
      <b>{title}</b>
      <span>{message}</span>
    </Alert>
  );
}
